import React from 'react'

import { Box, Collapse, Stack, Typography } from '@mui/material'

import { Card, FieldLabel, SectionNav, Skeleton } from '@repo/ui'
import { Search, StickyFormHeader, TocSpacer } from 'components'
import { vhMinus } from 'utils/textZoom'

import { FORM_SECTION_SX } from 'utils/formSectionSx'

const COLUMN_WIDTH = 720

/**
 * Loading placeholder for the product create/edit form (§19).
 *
 * ## What it replaces
 *
 * `{isLoading ? <Loader /> : …}` — a centred spinner in place of a ~2900px form: a 200px TOC rail,
 * a 167px sticky header with a search field, and six cards. Nothing was reserved.
 *
 * Only ever reached in EDIT mode. `useProduct` is `enabled: !!id`, and a disabled react-query
 * query is not `isLoading`, so the create route never takes this branch — which is also why the
 * skeleton reproduces the edit-mode section set (including Default tag).
 *
 * ## Construction first: most of this form is NOT data
 *
 * A create/edit form is unusual among detail pages in that nearly all of its structure is static —
 * the section list, the card titles, the field labels, the sticky header's title, the search box.
 * None of it depends on the fetch. So this renders the REAL components for all of it:
 *
 *   - the REAL `SectionNav`, fed the SAME `sections` array the page computes (passed in, not
 *     re-derived) — so the rail's height, labels, and the Back affordance are identical, and it
 *     tracks automatically if a section is added;
 *   - the REAL `StickyFormHeader` with the real title and the real `Search` field;
 *   - the REAL `Card` per section, with the real title and `info` flag;
 *   - the REAL `FieldLabel` per field, with the real label text — which is what makes each row's
 *     18px label and 8px label→control gap correct by construction rather than measured;
 *   - the REAL `Collapse` + `FORM_SECTION_SX` wrapper (shared via `utils/formSectionSx` — see that
 *     module for why it isn't exported from the page). That is load-bearing, not cosmetic: the
 *     section box's animation `from` state leaves a live `translateY(6px)` transform on the settled
 *     page, so a wrapper-less skeleton card sits 6px off the card it stands in for.
 *
 * What is left as a literal is only the CONTROL heights, which is exactly the part a loading state
 * cannot know: a text input, a rich-text editor at its `minHeight`, the dropzone, a button.
 *
 * ## The measured heights, and why each control's number is what it is
 *
 * Off `/products/:id/edit` (viewport 1711, form column x=624 w=720):
 *
 *   outer row        y=24  h=2432, `gap: 6`, centred; rail 200 at x=400, spacer 200 at x=1368
 *   sticky header    y=24  h=167   (its own 69px title block + a 49px search field)
 *   Media card       y=215 h=510   content 408
 *   Basic info       y=749 h=487   content 385
 *   Specifications   y=1260 h=789  content 687
 *   Scan instructions      h=483   content 381   ← DERIVED, not measured; see below
 *   Inventory        y=2073 h=177  content 75
 *   Default tag      y=2274 h=182  content 80
 *
 * ⚠️ **The `y=` column is STALE below Specifications, and that staleness IS the bug this
 * card fixes.** Those offsets sum exactly — 24 + 167 + 24 → 215, +510+24 → 749, +487+24 →
 * 1260, +789+24 → 2073 — with NO Scan card in the chain, so the page they were taken from
 * did not have one. VRG-323 shipped `id="scan"` between Specifications and Inventory
 * (`SECTIONS` in the page, and the rail already listed it because it reads that same array),
 * so the skeleton rendered five cards under a six-item rail and Inventory + Default tag were
 * shoved down the moment the fetch landed. Everything below Specifications now sits 507px
 * lower than the numbers above (the card's 483 plus one 24px column gap); re-measure the
 * whole column rather than trusting them.
 *
 * The Scan row's 381 is DERIVED FROM SOURCE CONSTANTS, not read off a browser, so it is
 * marked as such rather than quietly joining a table that claims to be measured:
 *
 *   intro copy         42   two 21px line boxes — `body3` is 14px and the real Typography
 *                           carries an explicit `lineHeight: 1.5` (§15)
 *   Instructions       123  20 (label row, inline `(optional)`) + 8 (FieldLabel) + 95 control
 *   Photo or video     168  20 + 8 + `ScanMediaField`'s own `FIELD_HEIGHT = 140`
 *   two 24px gaps      48
 *
 * The 95 is the one worth showing the arithmetic for. A single-line field is 49, of which
 * `1.4375em` — MUI's input line-height, which `TextField` records as computing to 23px at the
 * shared `FIELD_FONT_SIZE = 16` — is the inner row, leaving 26 of padding + border. The real
 * field is `multiline minRows={3}`, and `TextField.multilineSx` floors the inner box at
 * `calc(3 * 1.4375em)` = 69, so the control is 69 + 26 = 95.
 *
 *   Every card is `50 + 28 (title) + 24 + content`, so only `content` is reproduced here:
 *
 *   text input                49   (every TextField/Select/DatePicker on this form)
 *   media dropzone            268
 *   media preview row          90   ONE row — see under-reserves
 *   description editor        236   = 44 toolbar + 188 body + border, i.e. its `minHeight: 200px`
 *   details editor            436   = 44 toolbar + 388 body + border, i.e. its `minHeight: 400px`
 *   inline helper line box     20   (a FormHelperText beside a label, or under an editor)
 *
 * The two editor heights are FLOORS, not content measurements: both were measured on a product
 * whose description and details are one line each, so what they report is the `minHeight` the field
 * declares. They only under-reserve for genuinely long copy.
 *
 * ## Where it deliberately under-reserves
 *
 *   - the **media preview strip** reserves ONE 90px row. A product must have at least one image
 *     (the field validates it), so one row is the floor; a product with more grows the card.
 *   - the **Shopify "Synced from Shopify" alert** is not reserved — it renders only for a product
 *     with `integration_provider === 'shopify'`, and reserving it would put a hole above every
 *     other product's form.
 *   - the **Default tag** card reserves the "no tag set" layout (a line of copy + a button, 80px).
 *     A product that HAS a default tag renders a `ProductPreview` there instead, which is taller.
 */
export const ProductEditPageSkeleton = ({
  sections,
  onBack,
}: {
  sections: { id: string; label: string }[]
  onBack: () => void
}) => {
  /**
   * A form control: full width at the 49px height every SINGLE-LINE input on this form
   * renders at. `height` overrides that for the controls that are not single-line — today
   * only the Scan card's `minRows={3}` textarea (95) and its 140px media dropzone.
   */
  const InputSkeleton = ({ height = 49 }: { height?: number }) => (
    <Skeleton variant="rounded" height={height} />
  )

  /**
   * A FormHelperText's line box, as a bar. 20px is the height a helper occupies both inline beside
   * a label (which is why two rows on this form are 20px tall, not 18) and under a rich-text field.
   */
  const HelperSkeleton = ({ width }: { width: number }) => <Skeleton width={width} height={20} />

  /**
   * An inline helper beside a label — the reason the Serial Number and Product URL rows are 20px
   * tall rather than the label's own 18, and therefore why their fields are 77 rather than 75.
   *
   * The 20 is on the WRAPPER, as an explicit height. `lineHeight` was the first attempt and does
   * nothing here: the Skeleton inside is a block, so the inline-block box collapsed to the bar's own
   * 14px and the row stayed at the label's 18 — measured 75 against the real 77, twice over.
   */
  const inlineHelper = (width: number) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', height: 20 }}>
      <Skeleton width={width} height={14} />
    </Box>
  )

  /** One labelled field: the real label, a bar for the control. */
  const Field = ({
    label,
    adornment,
    height,
  }: {
    label: string
    adornment?: React.ReactNode
    height?: number
  }) => (
    <Box>
      <FieldLabel label={label} adornment={adornment} />
      <InputSkeleton height={height} />
    </Box>
  )

  /** A rich-text field: label, the editor at its declared minHeight, and its helper line. */
  const EditorField = ({
    label,
    height,
    helperWidth,
  }: {
    label: string
    height: number
    helperWidth: number
  }) => (
    <Box>
      <FieldLabel label={label} />
      <Skeleton variant="rounded" height={height} />
      <Box sx={{ mt: 1 }}>
        <HelperSkeleton width={helperWidth} />
      </Box>
    </Box>
  )

  /** The Collapse + animated section Box every card on the real page is wrapped in. */
  const Section = ({ id, children }: { id: string; children: React.ReactNode }) => (
    <Collapse in timeout={250} unmountOnExit>
      <Box id={id} sx={FORM_SECTION_SX}>
        {children}
      </Box>
    </Collapse>
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 6,
        width: '100%',
        mb: 6,
        justifyContent: 'center',
        alignItems: { xs: 'center', md: 'stretch' },
      }}
    >
      {/* The real rail, fed the page's own section list. `onChange` is a no-op — the TOC scrolls to
          a section, and there is nothing to scroll to yet. Back is wired to the page's real cancel
          so the one affordance a user might reach for during the load still works. */}
      <SectionNav
        items={sections.map((s) => ({ label: s.label, value: s.id }))}
        value={sections[0].id}
        onChange={() => {}}
        onBack={onBack}
      />

      <Box
        sx={{
          flex: 1,
          maxWidth: COLUMN_WIDTH,
          width: { xs: '100%', md: 'auto' },
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          minHeight: vhMinus(160),
        }}
      >
        <StickyFormHeader
          title="Edit Product"
          actions={
            <>
              {/* Cancel + Save, at the 40px header-action height and their measured widths. Bars
                  rather than real Buttons: Save would submit an empty form, and Cancel is already
                  reachable from the rail's Back. */}
              <Skeleton variant="rounded" width={78} height={40} sx={{ borderRadius: 2 }} />
              <Skeleton variant="rounded" width={71} height={40} sx={{ borderRadius: 2 }} />
            </>
          }
        >
          <Search onChange={() => {}} fullWidth={true} placeholder="Search fields..." />
        </StickyFormHeader>

        {/* MEDIA — label, dropzone, one preview row, at the real 16px gaps. */}
        <Section id="media">
          <Card title="Media" info={true}>
            <Stack spacing={4}>
              <Typography variant="body3" sx={{ display: 'block' }}>
                Product Images
              </Typography>
              <Skeleton variant="rounded" height={268} />
              <Skeleton variant="rounded" height={90} />
            </Stack>
          </Card>
        </Section>

        {/* BASIC INFO — the 2:1 name/type row, then the description editor. */}
        <Section id="basic">
          <Card title="Basic info" info={true}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
                <Box sx={{ flex: { xs: '1 1 100%', md: 2 }, minWidth: 240 }}>
                  <Field label="Product Name" />
                </Box>
                <Box sx={{ flex: { xs: '1 1 100%', md: 1 }, minWidth: 200 }}>
                  <Field label="Type" />
                </Box>
              </Box>
              <EditorField label="Description" height={236} helperWidth={330} />
            </Box>
          </Card>
        </Section>

        {/* SPECIFICATIONS — the three-up row, the URL field, then the details editor. */}
        <Section id="specs">
          <Card title="Specifications">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
                <Box
                  sx={{
                    flex: { xs: '1 1 100%', md: '1 1 calc(33.333% - 16px)' },
                    minWidth: '200px',
                  }}
                >
                  <Field label="Manufacture date" />
                </Box>
                <Box
                  sx={{
                    flex: { xs: '1 1 100%', md: '1 1 calc(33.333% - 16px)' },
                    minWidth: '200px',
                  }}
                >
                  <Field label="SKU" />
                </Box>
                <Box
                  sx={{
                    flex: { xs: '1 1 100%', md: '1 1 calc(33.333% - 16px)' },
                    minWidth: '200px',
                  }}
                >
                  {/* The real field carries an inline "(Optional)" helper, which makes this row's
                      label box 20px rather than 18 — and therefore the whole row 77, not 75. */}
                  <Field label="Serial Number" adornment={inlineHelper(52)} />
                </Box>
              </Box>
              {/* Same inline-helper story as Serial Number above. */}
              <Field label="Product URL" adornment={inlineHelper(317)} />
              <EditorField label="Product Details" height={436} helperWidth={470} />
            </Box>
          </Card>
        </Section>

        {/* SCAN INSTRUCTIONS (VRG-323) — intro copy, the multiline Instructions field, then the
            media dropzone. Sits between Specifications and Inventory because that is where
            `SECTIONS` puts it, and the rail above is fed that same array — so a missing card
            here is the one failure mode a rail-driven skeleton can still have: the nav lists a
            section the column does not reserve, and the fetch shoves the two cards below it. */}
        <Section id="scan">
          <Card title="Scan instructions">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* The intro paragraph, as its own child so the card's 24px gap separates it from
                  the first FIELD rather than from its own second line. 21px line boxes, not the
                  theme's 18: the real Typography overrides `lineHeight: 1.5` on 14px `body3`.
                  The height is on the WRAPPER for the reason the Default tag copy line below
                  records — the bar inside is a block, so `lineHeight` on it does nothing. */}
              <Box>
                {['100%', '62%'].map((width, i) => (
                  <Box
                    key={i}
                    sx={{ height: 21, display: 'flex', alignItems: 'center' }}
                  >
                    <Skeleton width={width} height={14} />
                  </Box>
                ))}
              </Box>

              {/* `helperText="optional"` does NOT render a helper line under this control — a
                  non-error `helperText` is routed into the LABEL ADORNMENT slot by `TextField`
                  (its "two message slots" note). So this is the Serial Number shape: a 20px
                  label row, not the label's own 18. */}
              <Field label="Instructions" adornment={inlineHelper(52)} height={95} />

              {/* `ScanMediaField` supplies its own `(optional)` adornment, over the dropzone at
                  its `FIELD_HEIGHT`. Reserves the EMPTY dropzone: a product that already has
                  media renders a preview at that same 140, so the two agree. */}
              <Field label="Photo or video" adornment={inlineHelper(52)} height={140} />
            </Box>
          </Card>
        </Section>

        {/* INVENTORY — availability toggle + quantity stepper, both a label over a 49px control. */}
        <Section id="inventory">
          <Card title="Inventory">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '40px' }}>
              <Box sx={{ width: 109 }}>
                <FieldLabel label="Availability" />
                <Stack sx={{ minHeight: '49px', justifyContent: 'center' }}>
                  <Skeleton variant="rounded" width={42} height={22} sx={{ borderRadius: '999px' }} />
                </Stack>
              </Box>
              <Box sx={{ width: 178 }}>
                <FieldLabel label="Quantity" />
                <Stack direction="row" gap="8px" alignItems="center" sx={{ minHeight: '49px' }}>
                  <Skeleton variant="rounded" width={82} height={49} />
                  <Skeleton variant="rounded" width={40} height={40} />
                  <Skeleton variant="rounded" width={40} height={40} />
                </Stack>
              </Box>
            </Box>
          </Card>
        </Section>

        {/* CONTACT CARD (VRG-720) — intro copy, the photo dropzone, three two-up rows of plain
            fields (each with `helperText="optional"` routed into a 20px label row, the Serial
            Number shape), then the social links label over the Add button (the empty layout: a
            card with links grows).
            ⚠️ The heights here are DERIVED from the same constants as the Scan card above, not
            measured in a browser: re-measure with the page open before trusting them. */}
        <Section id="contact">
          <Card title="Contact card">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <Box>
                {['100%', '78%'].map((width, i) => (
                  <Box key={i} sx={{ height: 21, display: 'flex', alignItems: 'center' }}>
                    <Skeleton width={width} height={14} />
                  </Box>
                ))}
              </Box>
              <Field label="Photo" adornment={inlineHelper(52)} height={140} />
              {[
                ['Name', 'Title'],
                ['Company', 'Website'],
                ['Phone', 'Email'],
              ].map(([a, b]) => (
                <Box key={a} sx={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
                  <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' }, minWidth: 240 }}>
                    <Field label={a} adornment={inlineHelper(52)} />
                  </Box>
                  <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' }, minWidth: 240 }}>
                    <Field label={b} adornment={inlineHelper(52)} />
                  </Box>
                </Box>
              ))}
              <Box>
                <FieldLabel label="Social links" adornment={inlineHelper(52)} />
                <Skeleton variant="rounded" width={163} height={40} sx={{ borderRadius: 2 }} />
              </Box>
            </Box>
          </Card>
        </Section>

        {/* DEFAULT TAG — the "not set" layout: one line of copy, then the action. */}
        <Section id="defaulttag">
          <Card title="Default tag">
            <Stack spacing={4} alignItems="flex-start">
              {/* The copy line's own 24px line box, as an explicit height on the wrapper — the bar
                  inside is a block, so `lineHeight` on the wrapper does nothing (measured: the box
                  collapsed to the bar's 16px and the card came out 8px short). */}
              <Box sx={{ height: 24, display: 'flex', alignItems: 'center' }}>
                <Skeleton width={520} height={16} />
              </Box>
              <Skeleton variant="rounded" width={163} height={40} sx={{ borderRadius: 2 }} />
            </Stack>
          </Card>
        </Section>
      </Box>

      <TocSpacer />
    </Box>
  )
}
